import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/Button';
import api from '../services/api';
import { EyeIcon, EyeOffIcon } from '../components/PasswordToggleIcon';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>Nephros</h1>
          <p style={styles.errorText}>Invalid or missing reset token. Please request a new password reset.</p>
          <Button onClick={() => navigate('/forgot-password')} style={{ width: '100%' }}>
            Request New Reset Link
          </Button>
        </div>
      </div>
    );
  }

  const rules = [
    { label: 'At least 12 characters',       met: password.length >= 12 },
    { label: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'At least one lowercase letter', met: /[a-z]/.test(password) },
    { label: 'At least one number',           met: /[0-9]/.test(password) },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 12) {
      setError('Password must be at least 12 characters.'); return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter.'); return;
    }
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter.'); return;
    }
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number.'); return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.'); return;
    }

    setLoading(true);
    try {
      await api.post('/auth/password-reset/confirm', {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? (detail[0]?.msg || '').replace(/^Value error,\s*/i, '')
        : detail;
      if (message?.includes('expired')) {
        setError('This reset link has expired. Please request a new one.');
      } else if (message?.includes('Invalid or already used') || message?.includes('invalid or has already been used')) {
        setError('This reset link is invalid or has already been used.');
      } else {
        setError(message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Nephros</h1>
          <p style={styles.subtitle}>CKD Care Coordination</p>
        </div>

        {success ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ ...styles.successText, marginBottom: 0 }}>
              Your password has been reset successfully.
            </p>
            <Button onClick={() => navigate('/login')} style={{ width: '100%' }}>
              Log In
            </Button>
          </div>
        ) : (
          <div>
            <p style={styles.instructionText}>Enter your new password below.</p>
            {error && <div style={styles.errorBanner}>{error}</div>}
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>New Password</label>

                {/* Password requirements — live feedback as user types */}
                <div style={styles.requirementsBlock}>
                  {rules.map(({ label, met }) => (
                    <div key={label} style={styles.requirementRow}>
                      <span style={met ? styles.checkMet : styles.checkUnmet}>
                        {met ? '✓' : '○'}
                      </span>
                      <span style={styles.requirementText}>{label}</span>
                    </div>
                  ))}
                  <div style={styles.requirementRow}>
                    <span style={styles.checkInfo}>◌</span>
                    <span style={styles.requirementTextMuted}>
                      Must not be a previously breached password — checked on submission
                    </span>
                  </div>
                </div>

                <div style={styles.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ ...styles.input, paddingRight: '40px' }}
                    autoFocus
                    required
                  />
                  {/* Eye toggle is an inline icon control — plain button intentional */}
                  <button type="button" style={styles.showHideButton} onClick={() => setShowPassword(p => !p)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}>
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Confirm New Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={styles.input}
                  required
                />
              </div>

              <Button type="submit" disabled={loading} style={{ width: '100%' }}>
                {loading ? 'Saving...' : 'Set New Password'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-bg-page)', fontFamily: 'var(--font-family-base)' },
  card: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: '48px', width: '100%', maxWidth: '400px' },
  header: { textAlign: 'center', marginBottom: '32px' },
  title: { fontSize: '28px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-nav)', margin: '0 0 4px 0', letterSpacing: '-0.5px', fontFamily: 'var(--font-family-base)' },
  subtitle: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
  instructionText: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: '24px', lineHeight: '1.5', fontFamily: 'var(--font-family-base)' },
  successText: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: '24px', lineHeight: '1.5', fontFamily: 'var(--font-family-base)' },
  errorBanner: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#991b1b', marginBottom: '20px', fontFamily: 'var(--font-family-base)' },
  errorText: { fontSize: 'var(--font-size-sm)', color: '#991b1b', marginBottom: '24px', fontFamily: 'var(--font-family-base)' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  input: { padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-family-base)' },
  passwordWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  showHideButton: { position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0', display: 'flex', alignItems: 'center' },
  requirementsBlock: { backgroundColor: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '6px' },
  requirementRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  checkMet: { fontSize: 'var(--font-size-xs)', color: '#059669', fontWeight: 'var(--font-weight-bold)', width: '14px', flexShrink: 0 },
  checkUnmet: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-placeholder)', width: '14px', flexShrink: 0 },
  checkInfo: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-placeholder)', width: '14px', flexShrink: 0 },
  requirementText: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  requirementTextMuted: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-placeholder)', fontStyle: 'italic', fontFamily: 'var(--font-family-base)' },
};